import { ButtonSecondary } from '../../../../../../../baseComponents/Button'
import { ModalBigCentered } from '../../../../../../../baseComponents/Modal'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { useCallback, useMemo } from 'react'
import { ModalOpenedSetTy } from '../../../../../../../baseComponents/Modal/types'
import { InputTableLayoutData, OrderHeaderURLs, SetCompleteDataTy } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { UserPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { flushSync } from 'react-dom'
import useEditDeleteModal from '../../../../../../../utils/hooks/useEditDeleteModal'
import { useParams } from 'react-router-dom'
import TableWithoutVirtualListing from '../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { ColumnTableSchema } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { DeleteRow } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { faPlus } from '@fortawesome/pro-light-svg-icons'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import TextArea from '../../../../../../../forms/Input/TextArea'
import { InputWithLabel } from '../../../../../../../forms/Input'
import { SellOrderAccountModelType } from '@teinor/erp/types/company/order/sell/sellOrder/sellOrderAccount'
import ModalDeleteSellOrderAccount from '../../../../../../../app/erp/screens/Logistic/QuoteParent/header/QuoteStepHeaderAccounts/ModalDeleteQuoteOrderAccount'
import { parseModalAccountsRowsData } from './functions'

interface ModalDivideIntoChaptersProps extends ModalEditComponentProps {
    isReadOnly?: boolean
    orderHeaderURLs: OrderHeaderURLs
    updateHeaderData: string
    headerData: TwinDictionary
    getCompleteData: () => void
    userPermissions: UserPermissions
    setCompleteData: SetCompleteDataTy
}

const ModalDivideIntoChaptersOrder: React.FC<ModalDivideIntoChaptersProps> = ({ isReadOnly, ...rest }) => {
    if (isReadOnly) {
        return <ModalDivideIntoChaptersOrderReadOnly { ...rest } />
    }
    return (<ModalDivideIntoChaptersOrderEdit { ...rest } />)
}

interface ModalDivideIntoChaptersOrderReadOnlyProps {
    setOpened: ModalOpenedSetTy
    setCompleteData: React.Dispatch<React.SetStateAction<InputTableLayoutData | null>>
    updateHeaderData: string
    headerData: TwinDictionary
}

const ModalDivideIntoChaptersOrderReadOnly: React.FC<ModalDivideIntoChaptersOrderReadOnlyProps> = ({ headerData, setOpened }) => {
    const { t } = useTwinTranslation()
    const accounts = useMemo(() => parseModalAccountsRowsData(headerData?.OrderAccounts), [headerData?.OrderAccounts])
    const columns: ColumnTableSchema<SellOrderAccountModelType> = [
        {
            id: 'name',
            dataKey: 'name',
            label: t('chapter', 'Capítulo'),
        },
        {
            id: 'description',
            dataKey: 'description',
            label: t('description', 'Description'),
        },
    ]

    return (
        <ModalBigCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_chapter_sell gap-y-19 flex_height'>
            <h2 className='flex justify-start regular20'><TwinTrans transKey='chapters'>Capítulos</TwinTrans></h2>
            <div className='mt-20 flex flex-auto flex-col'>
                <div className='flex-auto overflow-visible'>
                    <TableWithoutVirtualListing data={accounts} columns={columns} className='min-h-300 add_chapter_table' />
                </div>
                <div className='flex justify-end gap-x-33'>
                    <ButtonSecondary onClick={() => setOpened(null)} className='regular14 mt-10'>
                        <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                    </ButtonSecondary>
                </div>
            </div>
        </ModalBigCentered>
    )
}

const ModalDivideIntoChaptersOrderEdit: React.FC<ModalDivideIntoChaptersProps> = ({ headerData, orderHeaderURLs, setOpened, setCompleteData, getCompleteData }) => {
    const { t } = useTwinTranslation()
    const { myHandleChange, openModal, setOpenModal, accounts, addRow } = useModalDivideIntoChaptersEditLogic({ setCompleteData, orderHeaderURLs, headerData })
    const columns: ColumnTableSchema<SellOrderAccountModelType> = [
        {
            id: 'name',
            dataKey: 'name',
            label: t('chapter', 'Capítulo'),
            customRender: (value, rowData) => <InputWithLabel label='' defaultValue={rowData?.name} onChange={(value) => myHandleChange(String(rowData?.rowIndex), 'name', value)} />
        },
        {
            id: 'description',
            dataKey: 'description',
            label: t('description', 'Description'),
            customRender: (value, rowData) => <TextArea value={rowData?.description} onChange={(value) => myHandleChange(String(rowData?.rowIndex), 'description', value)}/>
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + allRowData.rowIndex} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpenModal({
                type: 'delete',
                allRowData: { 'name': allRowData.name, 'id': allRowData.id, 'OrderId': headerData.id }
            })} />,
        }
    ]

    return (
        <ModalBigCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_chapter_sell gap-y-19 flex_height'>
            <h2 className='flex justify-start regular20 mb-10'><TwinTrans transKey='addEditChapters'>Añadir o editar capítulos</TwinTrans></h2>
            <div className='mt-20 flex flex-auto flex-col'>
                <div className='flex-auto max-h-300 overflow-auto'>
                    <TableWithoutVirtualListing data={accounts} columns={columns} className='add_chapter_table' disableScroll={true} />
                    <div className='cursor-pointer text-center bg-gray-EF py-4 w-full text-gray-51' onClick={addRow}>
                        <TwinIcon className='mr-10' icon={faPlus} />
                        <TwinTrans transKey='addRow'>Añadir fila</TwinTrans>
                    </div>
                </div>
                <div className='flex justify-end gap-x-33'>
                    <ButtonSecondary onClick={() => setOpened(null)} className='regular14 mt-10'>
                        <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                    </ButtonSecondary>
                </div>
            </div>
            {openModal?.type === 'delete' ? <ModalDeleteSellOrderAccount getCompleteData={getCompleteData} orderHeaderURLs={orderHeaderURLs} setOpened={() => setOpenModal(null)} onSubmit={() => {}} allRowData={openModal.allRowData} /> : null}
        </ModalBigCentered>
    )
}

interface ModalDivideIntoChaptersEditLogicProps {
    setCompleteData: SetCompleteDataTy
    orderHeaderURLs: OrderHeaderURLs
    headerData: TwinDictionary
}

const useModalDivideIntoChaptersEditLogic = ({ orderHeaderURLs, setCompleteData, headerData}: ModalDivideIntoChaptersEditLogicProps) => {
    const { t } = useTwinTranslation()
    const params = useParams<{ id: string }>()
    const accounts = useMemo(() => parseModalAccountsRowsData(headerData?.OrderAccounts), [headerData?.OrderAccounts])
    const id = params.id
    const chapterText = t('chapter', 'Capítulo')
    const { openModal, setOpenModal } = useEditDeleteModal()

    const addRow = useCallback(async () => {
        const accountLength = accounts.length
        const order = parseInt(String(accounts[accountLength - 1]?.order || '0')) + 1
        const account = await twinFetchPostJSON(orderHeaderURLs.createAccount, {
            name: chapterText + ' ' + order,
            OrderId: id
        })
        setCompleteData((old) => {
            if (!old) {
                return null
            }
            const myData = JSON.parse(JSON.stringify(old))
            myData?.header?.OrderAccounts?.push(account)
            return { ...myData }
        })
    }, [id, setCompleteData, orderHeaderURLs, chapterText, accounts])

    const myHandleChange = useCallback(async (index: string, field: string, value: any) => {
        await flushSync(async () => {
            let account: TwinDictionary = {}
            await setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                myData.header.OrderAccounts[index][field] = value
                account = myData.header.OrderAccounts[index]
                return { ...myData }
            })
            if (account) {
                await twinFetchPostJSON(orderHeaderURLs.updateAccount, { ...account })
            }
        })
    }, [setCompleteData, orderHeaderURLs])

    return { myHandleChange, openModal, setOpenModal, accounts, addRow }
}

export default ModalDivideIntoChaptersOrder